import { useUserContext } from '@/api/hooks/useUserContext';
import { useAuthStore } from '@/stores/auth.store';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { Page, Preloader, f7 } from 'framework7-react';
import { useEffect, useMemo } from 'react';

/** Two initials from a restaurant name for the avatar (e.g. "The Olive Branch" → "OB"). */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** Deterministic hue from the name so each restaurant keeps a stable avatar tint. */
function hue(name: string): number {
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum = (sum + name.charCodeAt(i)) % 360;
  return sum;
}

function SelectRestaurantPage() {
  const { data: userContext, isLoading, error, refetch, isFetching } = useUserContext();
  const { selectRestaurant, selectedRestaurantId } = useRestaurantStore();
  const deniedRestaurantIds = useRestaurantStore((s) => s.deniedRestaurantIds);
  const logout = useAuthStore((s) => s.logout);

  const handleSignOut = () => {
    logout();
    f7.views.main.router.navigate('/login', { reloadAll: true });
  };

  const workers = useMemo(() => userContext?.workers ?? [], [userContext?.workers]);

  // Restaurants the API already refused this session are excluded: re-selecting
  // one only bounces back here from BootstrapGate, and auto-select would turn
  // that bounce into an infinite mount loop firing a request every ~300ms.
  const allRestaurants = useMemo(
    () =>
      workers.flatMap((w) =>
        w.restaurants
          .filter((r) => !deniedRestaurantIds.includes(r.restaurantId))
          .map((r) => ({
            franchiseId: w.franchiseId,
            restaurantId: r.restaurantId,
          })),
      ),
    [workers, deniedRestaurantIds],
  );

  const onlyOne = allRestaurants.length === 1;

  // Auto-skip when the user has exactly one restaurant - straight to the
  // dashboard, no selection step. (Render the loader, not the list, so the
  // single row never flashes before the redirect fires below.)
  useEffect(() => {
    if (onlyOne) {
      selectRestaurant(allRestaurants[0].franchiseId, allRestaurants[0].restaurantId);
      f7.views.main.router.navigate('/', { reloadAll: true });
    }
  }, [onlyOne, allRestaurants, selectRestaurant]);

  // If already selected (e.g. returning to this route), redirect.
  useEffect(() => {
    if (selectedRestaurantId) {
      f7.views.main.router.navigate('/', { reloadAll: true });
    }
  }, [selectedRestaurantId]);

  const handleSelect = (franchiseId: string, restaurantId: string) => {
    selectRestaurant(franchiseId, restaurantId);
    f7.views.main.router.navigate('/', { reloadAll: true });
  };

  // Loading - also covers the brief moment while the single-restaurant or
  // already-selected redirect is in flight, so no half-built UI flashes.
  if (isLoading || (!userContext && !error) || onlyOne || selectedRestaurantId) {
    return (
      <Page name="select-restaurant" noNavbar noToolbar>
        <div className="pick-page">
          <div className="pick-loading">
            <Preloader size={34} />
            <p className="pick-loading__text">Loading your restaurants…</p>
          </div>
        </div>
      </Page>
    );
  }

  // Nothing to offer: no memberships at all, or every restaurant we know of was
  // refused by the API - either way the picker has no valid row to render.
  if (error || allRestaurants.length === 0) {
    return (
      <Page name="select-restaurant" noNavbar noToolbar>
        <div className="pick-page">
          <div className="pick-card">
            <div className="pick-brand">
              <div className="pick-brand__mark">
                <i className="f7-icons">house_fill</i>
              </div>
              <h1 className="pick-brand__title">No restaurants found</h1>
              <p className="pick-brand__subtitle">
                {error
                  ? 'We couldn’t load your account. Please try again.'
                  : 'Your account doesn’t have access to any restaurants yet. Ask an owner to invite you, or sign in with a different account.'}
              </p>
            </div>

            <div className="pick-actions">
              <button
                type="button"
                className="pick-btn pick-btn--primary"
                disabled={isFetching}
                onClick={() => refetch()}
              >
                {isFetching ? 'Checking…' : 'Try again'}
              </button>
              <button type="button" className="pick-btn pick-btn--ghost" onClick={handleSignOut}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page name="select-restaurant" noNavbar noToolbar>
      <div className="pick-page">
        <div className="pick-card">
          <div className="pick-brand">
            <div className="pick-brand__mark">
              <i className="f7-icons">house_fill</i>
            </div>
            <h1 className="pick-brand__title">Choose a restaurant</h1>
            <p className="pick-brand__subtitle">Pick the location you’d like to manage.</p>
          </div>

          {workers.map((worker) => (
            <div key={worker.franchiseId} className="pick-group">
              {workers.length > 1 && <p className="pick-group__label">{worker.franchiseName}</p>}
              <div className="pick-list">
                {worker.restaurants
                  .filter((r) => !deniedRestaurantIds.includes(r.restaurantId))
                  .map((restaurant) => {
                    const h = hue(restaurant.restaurantName);
                    return (
                      <button
                        key={restaurant.restaurantId}
                        type="button"
                        className="pick-row"
                        onClick={() => handleSelect(worker.franchiseId, restaurant.restaurantId)}
                      >
                        <span
                          className="pick-row__avatar"
                          style={{
                            background: `linear-gradient(140deg, hsl(${h} 70% 52%), hsl(${(h + 28) % 360} 72% 44%))`,
                          }}
                        >
                          {initials(restaurant.restaurantName)}
                        </span>
                        <span className="pick-row__body">
                          <span className="pick-row__name">{restaurant.restaurantName}</span>
                          {restaurant.role && (
                            <span className="pick-row__role">{restaurant.role}</span>
                          )}
                        </span>
                        <i className="f7-icons pick-row__chevron">chevron_right</i>
                      </button>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Page>
  );
}

export default SelectRestaurantPage;
